/**
 * Mapping ClinicType (Prisma) → URL pattern + label public.
 * Source unique de vérité pour catégoriser les établissements de santé.
 *
 * Pattern Doctolib :
 *   - hôpital public        → /hopital/{slug}
 *   - clinique privée       → /clinique/{slug}
 *   - centre médical        → /centre-medical/{slug}
 *   - polyclinique          → /polyclinique/{slug}
 *   - centre dentaire       → /centre-dentaire/{slug}
 *   - clinique esthétique   → /clinique-esthetique/{slug}
 *   - centre PMA            → /centre-pma/{slug}
 *   - centre rééducation    → /centre-reeducation/{slug}
 *   - centre radiologie     → /centre-radiologie/{slug}
 *   - laboratoire           → /laboratoire/{slug}
 *   - clinique vétérinaire  → /clinique-veterinaire/{slug}
 *
 * Les médecins individuels restent sur /medecin/{slug}.
 */
export type ClinicTypeKey =
  | "PUBLIC_HOSPITAL"
  | "PRIVATE_CLINIC"
  | "MEDICAL_CENTER"
  | "POLYCLINIC"
  | "DENTAL_CLINIC"
  | "AESTHETIC_CLINIC"
  | "IVF_CENTER"
  | "REHAB_CENTER"
  | "RADIOLOGY_CENTER"
  | "LAB"
  | "VETERINARY_CLINIC";

type CenterRoute = {
  /** Segment d'URL public (sans / initial). */
  pathSegment: string;
  /** Label affiché (FR) — utilisé sur la fiche et les listings. */
  labelFr: string;
  /** Label arabe (RTL). */
  labelAr: string;
  /** Label anglais. */
  labelEn: string;
  /** Emoji décoratif (header de fiche). */
  emoji: string;
};

export const CENTER_ROUTES: Record<ClinicTypeKey, CenterRoute> = {
  PUBLIC_HOSPITAL: {
    pathSegment: "hopital",
    labelFr: "Hôpital public",
    labelAr: "مستشفى عمومي",
    labelEn: "Public hospital",
    emoji: "🏥",
  },
  PRIVATE_CLINIC: {
    pathSegment: "clinique",
    labelFr: "Clinique privée",
    labelAr: "عيادة خاصة",
    labelEn: "Private clinic",
    emoji: "🏥",
  },
  MEDICAL_CENTER: {
    pathSegment: "centre-medical",
    labelFr: "Centre médical",
    labelAr: "مركز طبي",
    labelEn: "Medical center",
    emoji: "⚕️",
  },
  POLYCLINIC: {
    pathSegment: "polyclinique",
    labelFr: "Polyclinique",
    labelAr: "متعدد التخصصات",
    labelEn: "Polyclinic",
    emoji: "🏥",
  },
  DENTAL_CLINIC: {
    pathSegment: "centre-dentaire",
    labelFr: "Centre dentaire",
    labelAr: "مركز طب الأسنان",
    labelEn: "Dental center",
    emoji: "🦷",
  },
  AESTHETIC_CLINIC: {
    pathSegment: "clinique-esthetique",
    labelFr: "Clinique esthétique",
    labelAr: "عيادة تجميل",
    labelEn: "Aesthetic clinic",
    emoji: "💉",
  },
  IVF_CENTER: {
    pathSegment: "centre-pma",
    labelFr: "Centre PMA / FIV",
    labelAr: "مركز الإخصاب",
    labelEn: "IVF center",
    emoji: "🤰",
  },
  REHAB_CENTER: {
    pathSegment: "centre-reeducation",
    labelFr: "Centre de rééducation",
    labelAr: "مركز إعادة التأهيل",
    labelEn: "Rehab center",
    emoji: "🧘",
  },
  RADIOLOGY_CENTER: {
    pathSegment: "centre-radiologie",
    labelFr: "Centre de radiologie",
    labelAr: "مركز الأشعة",
    labelEn: "Radiology center",
    emoji: "📡",
  },
  LAB: {
    pathSegment: "laboratoire",
    labelFr: "Laboratoire d'analyses",
    labelAr: "مختبر تحاليل",
    labelEn: "Medical lab",
    emoji: "🧪",
  },
  VETERINARY_CLINIC: {
    pathSegment: "clinique-veterinaire",
    labelFr: "Clinique vétérinaire",
    labelAr: "عيادة بيطرية",
    labelEn: "Veterinary clinic",
    emoji: "🐾",
  },
};

/** URL canonique pour un centre selon son type ClinicType. */
export function centerUrl(slug: string, type: ClinicTypeKey | null | undefined): string {
  const segment = type ? CENTER_ROUTES[type]?.pathSegment : "centre";
  return `/${segment ?? "centre"}/${slug}`;
}

/**
 * Infere le ClinicTypeKey depuis un physician static (isCenter=true).
 * Static n'a pas de field `clinicType` (c'est en DB uniquement).
 * On heuristique sur specialty + centerName.
 *
 * Audit 2026-05-11 : route canonique d'un centre dentaire doit etre
 * `/centre-dentaire/{slug}` pas `/centre/{slug}` (generic).
 */
export function inferClinicType(p: {
  specialties?: ReadonlyArray<string> | string[];
  centerName?: string | null;
  lastName?: string;
}): ClinicTypeKey | null {
  const spe = (p.specialties ?? [])[0] ?? "";
  const name = (p.centerName ?? p.lastName ?? "").toLowerCase();

  // 1. Mapping direct par specialty
  const SPE_MAP: Record<string, ClinicTypeKey> = {
    "dentiste": "DENTAL_CLINIC",
    "chirurgien-dentiste": "DENTAL_CLINIC",
    "orthodontiste": "DENTAL_CLINIC",
    "stomatologue": "DENTAL_CLINIC",
    "chirurgien-esthetique": "AESTHETIC_CLINIC",
    "medecin-esthetique": "AESTHETIC_CLINIC",
    "chirurgien-plasticien": "AESTHETIC_CLINIC",
    "radiologue": "RADIOLOGY_CENTER",
    "biologiste": "LAB",
    "anatomopathologiste": "LAB",
    "kinesitherapeute": "REHAB_CENTER",
    "osteopathe": "REHAB_CENTER",
    "podologue": "REHAB_CENTER",
    "veterinaire": "VETERINARY_CLINIC",
  };
  if (spe && SPE_MAP[spe]) return SPE_MAP[spe]!;

  // 2. Heuristique sur centerName/lastName
  if (/h[oô]pital|hospital/i.test(name)) return "PUBLIC_HOSPITAL";
  if (/polyclinique|polyclinic/i.test(name)) return "POLYCLINIC";
  if (/v[eé]t[eé]rinaire|veterinary/i.test(name)) return "VETERINARY_CLINIC";
  if (/laboratoire|laboratory|analyses/i.test(name)) return "LAB";
  if (/radiologie|imagerie|radiology|imaging/i.test(name)) return "RADIOLOGY_CENTER";
  if (/dentaire|dental|orthodonti|implant/i.test(name)) return "DENTAL_CLINIC";
  if (/esth[eé]tique|aesthetic|cosmetic|laser/i.test(name)) return "AESTHETIC_CLINIC";
  if (/pma|fiv|fertilit/i.test(name)) return "IVF_CENTER";
  if (/r[eé][eé]ducation|rehab|kine|kinesi|osteo/i.test(name)) return "REHAB_CENTER";
  if (/centre m[eé]dical|medical center/i.test(name)) return "MEDICAL_CENTER";
  if (/clinique|clinic/i.test(name)) return "PRIVATE_CLINIC";

  return null;
}

/** Label localisé pour le type de centre. */
export function centerLabel(
  type: ClinicTypeKey | null | undefined,
  locale: "fr" | "ar" | "en" = "fr",
): string {
  const def = type ? CENTER_ROUTES[type] : null;
  if (!def) return "Établissement de santé";
  return locale === "ar" ? def.labelAr : locale === "en" ? def.labelEn : def.labelFr;
}
